import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../services/api';
import { Item, InventoryPrecheckResponse } from '../../services/types';
import { cn } from '../../lib/utils';
import EntityTypeaheadPicker, { PickerOption } from '../../components/search/EntityTypeaheadPicker';
import { RequiredLabel } from '../../components/forms/RequiredLabel';
import { FieldErrorText } from '../../components/forms/FieldErrorText';
import { showValidationSummaryToast } from '../../components/forms/ValidationSummaryToast';
import { focusFirstInvalidField } from '../../components/forms/formValidationUtils';

const schema = z.object({
    item_id: z.string().min(1, 'Item is required'),
    quantity: z.number().min(0.01, 'Quantity must be positive'),
    reference_doc: z.string().min(1, 'Reference is required'),
});

type FormData = z.infer<typeof schema>;

interface Props {
    isOpen: boolean;
    initialItemId?: number | null;
    onClose: () => void;
    onSuccess: () => void;
}

export function ReceiveStockModal({ isOpen, initialItemId = null, onClose, onSuccess }: Props) {
    const [items, setItems] = useState<Item[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState<'all' | 'raw'>('all');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [profileFilter, setProfileFilter] = useState<'all' | 'GRC' | 'GRP' | 'BOM'>('all');
    const [precheckResult, setPrecheckResult] = useState<InventoryPrecheckResponse | null>(null);

    const { register, handleSubmit, setValue, watch, formState: { errors, isSubmitting } } = useForm<FormData>({
        resolver: zodResolver(schema),
        defaultValues: {
            item_id: '',
            quantity: 0,
            reference_doc: '',
        },
    });

    const selectedItemId = watch('item_id');

    const normalizeItemType = (value?: string | null): string => String(value || 'Stock').trim().toLowerCase().replace(/[-_\s]/g, '');
    const isStockValuedItem = (item: Item): boolean => {
        const role = (item.item_role || '').toUpperCase();
        const type = normalizeItemType(item.item_type);
        return type === 'stock' && !['SERVICE', 'CONSUMABLE', 'NON_STOCK', 'NONSTOCK', 'ASSET', 'FIXED_ASSET'].includes(role);
    };
    const isFinishedGood = (item: Item): boolean => (item.item_role || '').toUpperCase() === 'FG';
    const isRaw = (item: Item): boolean => isStockValuedItem(item) && !isFinishedGood(item);

    const stockItems = useMemo(() => items.filter((item) => isStockValuedItem(item) && !isFinishedGood(item)), [items]);

    const categoryOptions = useMemo(
        () => Array.from(new Set(stockItems.map((item) => String(item.category || '').trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b)),
        [stockItems]
    );

    const profileOptions = useMemo(() => {
        const profiles = new Set<string>();
        stockItems.forEach((item) => {
            (item.cost_profiles || []).forEach((profile) => profiles.add(String(profile).toUpperCase()));
            const category = String(item.category || '').toUpperCase();
            if (category === 'GRC' || category === 'GRP' || category === 'BOM') {
                profiles.add(category);
            }
        });
        return Array.from(profiles).sort() as Array<'GRC' | 'GRP' | 'BOM'>;
    }, [stockItems]);

    const filteredItems = useMemo(() => {
        return stockItems.filter((item) => {
            const q = searchTerm.trim().toLowerCase();
            if (q) {
                const haystack = `${item.code} ${item.name} ${item.category || ''}`.toLowerCase();
                if (!haystack.includes(q)) return false;
            }
            if (roleFilter === 'raw' && !isRaw(item)) return false;
            if (categoryFilter !== 'all' && String(item.category || '') !== categoryFilter) return false;
            if (profileFilter !== 'all') {
                const itemProfiles = new Set((item.cost_profiles || []).map((profile) => String(profile).toUpperCase()));
                const fallback = String(item.category || '').toUpperCase();
                if (!itemProfiles.has(profileFilter) && fallback !== profileFilter) return false;
            }
            return true;
        });
    }, [stockItems, searchTerm, roleFilter, categoryFilter, profileFilter]);

    const itemPickerOptions = useMemo<PickerOption<string>[]>(() => {
        return filteredItems.map((item) => {
            const role = isFinishedGood(item) ? 'FG' : 'RAW';
            const profiles = (item.cost_profiles || []).map((profile) => String(profile).toUpperCase()).filter(Boolean);
            return {
                value: String(item.id),
                label: `${item.code} - ${item.name}`,
                description: item.category || 'Uncategorized',
                badges: [
                    { label: role, tone: role === 'FG' ? 'blue' : 'slate' },
                    ...profiles.slice(0, 2).map((profile) => ({ label: profile, tone: 'green' as const })),
                ],
            };
        });
    }, [filteredItems]);

    useEffect(() => {
        api.inventory.list().then(setItems);
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        setPrecheckResult(null);
        if (initialItemId) {
            setValue('item_id', String(initialItemId), { shouldValidate: true });
            return;
        }
        setValue('item_id', '');
    }, [isOpen, initialItemId, setValue]);

    useEffect(() => {
        if (!selectedItemId) return;
        if (stockItems.length === 0) return;
        const exists = stockItems.some((item) => String(item.id) === String(selectedItemId));
        if (!exists) setValue('item_id', '');
    }, [stockItems, selectedItemId, setValue]);

    const onSubmit = async (data: FormData) => {
        try {
            const precheck = await api.inventory.precheckInternalReceipt({
                item_id: parseInt(data.item_id, 10),
                quantity: data.quantity,
                reference_doc: data.reference_doc,
            });
            setPrecheckResult(precheck);
            if (String(precheck.overall_status || '').toLowerCase() === 'fail') {
                toast.error(precheck.checks?.find((check) => String(check.status).toLowerCase() === 'fail')?.message || 'Precheck failed');
                return;
            }
            if (String(precheck.overall_status || '').toLowerCase() === 'warn') {
                toast.warning('Precheck warning: review flow impact before confirming.');
            }

            await api.inventory.createInternalReceipt({
                item_id: parseInt(data.item_id, 10),
                quantity: data.quantity,
                reference_doc: data.reference_doc,
            });
            onSuccess();
        } catch (error) {
            console.error('Failed to update stock', error);
            toast.error('Failed to save stock update');
        }
    };
    const onInvalid = (formErrors: Record<string, any>) => {
        showValidationSummaryToast();
        focusFirstInvalidField(Object.keys(formErrors)[0]);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-lg shadow-xl w-[min(96vw,1080px)] mx-4 max-h-[92vh] overflow-y-auto">
                <div className="flex justify-between items-center p-4 border-b">
                    <h2 className="text-lg font-semibold">Internal Stock Update</h2>
                    <button onClick={onClose} className="text-slate-500 hover:text-slate-700">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="p-5 space-y-5">
                    <div className="rounded-md border bg-slate-50 p-3 space-y-1">
                        <p className="text-xs text-slate-600">
                            Use this for controlled internal stock updates on raw/material stock (opening balances, corrections).
                        </p>
                        <p className="text-xs text-slate-500">
                            Finished goods use source-linked `Post FG Movement`. Supplier receipts use PO/GRN flow.
                        </p>
                        <p className="text-xs text-slate-500">
                            Flow impact: internal updates affect inventory only; no accounting journal is posted.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
                        <div className="space-y-2 lg:col-span-3">
                            <RequiredLabel required>Item</RequiredLabel>
                            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-3">
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full rounded border border-slate-300 p-2 text-sm bg-white"
                                    placeholder="Search code/name/category..."
                                    aria-label="Search Items"
                                />

                                <div className="space-y-2">
                                    <div className="text-xs font-medium text-slate-600">Stock Role</div>
                                    <div className="flex flex-wrap gap-2">
                                        {([
                                            { value: 'all', label: 'All' },
                                            { value: 'raw', label: 'Raw Materials' },
                                        ] as const).map((option) => (
                                            <button
                                                key={option.value}
                                                type="button"
                                                onClick={() => setRoleFilter(option.value)}
                                                className={cn(
                                                    'rounded-full px-3 py-1 text-xs font-medium border transition-colors',
                                                    roleFilter === option.value
                                                        ? 'bg-blue-600 text-white border-blue-600'
                                                        : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100'
                                                )}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <div className="text-xs font-medium text-slate-600">Cost Profile</div>
                                    <div className="flex flex-wrap gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setProfileFilter('all')}
                                            className={cn(
                                                'rounded-full px-3 py-1 text-xs font-medium border transition-colors',
                                                profileFilter === 'all'
                                                    ? 'bg-blue-600 text-white border-blue-600'
                                                    : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100'
                                            )}
                                        >
                                            All
                                        </button>
                                        {profileOptions.map((profile) => (
                                            <button
                                                key={profile}
                                                type="button"
                                                onClick={() => setProfileFilter(profile)}
                                                className={cn(
                                                    'rounded-full px-3 py-1 text-xs font-medium border transition-colors',
                                                    profileFilter === profile
                                                        ? 'bg-blue-600 text-white border-blue-600'
                                                        : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100'
                                                )}
                                            >
                                                {profile}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <div className="text-xs font-medium text-slate-600">Category</div>
                                    <div className="max-h-24 overflow-y-auto pr-1">
                                        <div className="flex flex-wrap gap-2">
                                            <button
                                                type="button"
                                                onClick={() => setCategoryFilter('all')}
                                                className={cn(
                                                    'rounded-full px-3 py-1 text-xs font-medium border transition-colors',
                                                    categoryFilter === 'all'
                                                        ? 'bg-blue-600 text-white border-blue-600'
                                                        : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100'
                                                )}
                                            >
                                                All Categories
                                            </button>
                                            {categoryOptions.map((category) => (
                                                <button
                                                    key={category}
                                                    type="button"
                                                    onClick={() => setCategoryFilter(category)}
                                                    className={cn(
                                                        'rounded-full px-3 py-1 text-xs font-medium border transition-colors',
                                                        categoryFilter === category
                                                            ? 'bg-blue-600 text-white border-blue-600'
                                                            : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100'
                                                    )}
                                                >
                                                    {category}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-end">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSearchTerm('');
                                            setRoleFilter('all');
                                            setProfileFilter('all');
                                            setCategoryFilter('all');
                                        }}
                                        className="text-xs font-medium text-slate-600 hover:text-slate-900"
                                    >
                                        Clear Filters
                                    </button>
                                </div>
                            </div>

                            <input type="hidden" {...register('item_id')} />
                            <div className={cn(errors.item_id && 'rounded-md border border-red-500 p-1')}>
                                <EntityTypeaheadPicker
                                    value={selectedItemId || ''}
                                    options={itemPickerOptions}
                                    placeholder="Search and select stock item..."
                                    emptyText="No matching stock items"
                                    menuPlacement="top"
                                    onChange={(value) => setValue('item_id', value, { shouldValidate: true, shouldDirty: true })}
                                />
                            </div>
                            <p className="text-xs text-slate-500">
                                Showing {filteredItems.length} of {stockItems.length} stock items
                            </p>
                            {selectedItemId && (
                                <p className="text-xs text-slate-500">
                                    Source context: use SO/WO/Count reference to keep stock audit trace clear.
                                </p>
                            )}
                            {!filteredItems.length && (
                                <p className="text-xs text-amber-600">No items match current filters. Clear filters or search text.</p>
                            )}
                            <FieldErrorText id="item_id_error" message={errors.item_id?.message} />
                        </div>

                        <div className="lg:col-span-2">
                            <div className="rounded-md border border-slate-200 bg-white p-4 space-y-4">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <RequiredLabel required htmlFor="quantity">Quantity</RequiredLabel>
                                        <input
                                            id="quantity"
                                            type="number"
                                            step="0.01"
                                            {...register('quantity', { valueAsNumber: true })}
                                            aria-invalid={!!errors.quantity}
                                            aria-required="true"
                                            aria-describedby={errors.quantity ? 'quantity_error' : undefined}
                                            className="w-full rounded border border-slate-300 p-2 aria-[invalid=true]:border-red-500 aria-[invalid=true]:ring-1 aria-[invalid=true]:ring-red-100"
                                        />
                                        <FieldErrorText id="quantity_error" message={errors.quantity?.message} />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <RequiredLabel required htmlFor="reference_doc">Reference / Reason</RequiredLabel>
                                    <input
                                        id="reference_doc"
                                        type="text"
                                        {...register('reference_doc')}
                                        aria-invalid={!!errors.reference_doc}
                                        aria-required="true"
                                        aria-describedby={errors.reference_doc ? 'reference_doc_error' : undefined}
                                        className="w-full rounded border border-slate-300 p-2 aria-[invalid=true]:border-red-500 aria-[invalid=true]:ring-1 aria-[invalid=true]:ring-red-100"
                                        placeholder="WO-123 / SO-45 / Opening balance"
                                    />
                                    <FieldErrorText id="reference_doc_error" message={errors.reference_doc?.message} />
                                </div>

                                {precheckResult && (
                                    <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-1">
                                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                                            Precheck: {String(precheckResult.overall_status || 'pass').toUpperCase()}
                                        </p>
                                        <p className="text-xs text-slate-600">Inventory: {precheckResult.impact?.inventory}</p>
                                        <p className="text-xs text-slate-600">Finance: {precheckResult.impact?.finance}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-2 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded hover:bg-slate-200"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
                        >
                            {isSubmitting ? 'Saving...' : 'Save Update'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
